import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, QrCode, RefreshCw, Copy, Check, Download, Clock, Package } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface ClientPickupQRCodeProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  orderNumber: string;
  productTitle: string;
  orderStatus: string;
}

export const ClientPickupQRCode = ({ 
  open, 
  onClose, 
  orderId, 
  orderNumber, 
  productTitle,
  orderStatus 
}: ClientPickupQRCodeProps) => {
  const [loading, setLoading] = useState(false);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && orderId) {
      fetchOrGenerateQRCode();
    }
  }, [open, orderId]);

  useEffect(() => {
    if (!open) {
      setQrSvg(null);
      setOtpCode(null);
      setExpiresAt(null);
      setError(null);
    }
  }, [open]);

  const fetchOrGenerateQRCode = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, try to fetch existing token
      const { data: existingToken, error: fetchError } = await supabase
        .from('pickup_tokens')
        .select('qr_svg, otp_expires_at')
        .eq('order_id', orderId)
        .maybeSingle();

      if (fetchError) {
        console.error("Error fetching token:", fetchError);
        setError("Erreur lors de la récupération du code de retrait");
        return;
      }

      if (existingToken?.qr_svg) {
        // Even if a token already exists (pre-created at payment), don't reveal it
        // before the vendor has marked the order as ready for pickup.
        if (orderStatus !== 'AVAILABLE_FOR_PICKUP') {
          setError("Votre commande est confirmée. Le QR code sera disponible dès que le vendeur aura déposé le produit au point relais.");
          return;
        }
        setQrSvg(existingToken.qr_svg);
        setExpiresAt(existingToken.otp_expires_at);
        return;
      }

      // Only generate/show QR when the vendor has confirmed the order is ready
      // (status AVAILABLE_FOR_PICKUP). For PAID orders we wait for the vendor.
      if (orderStatus === 'AVAILABLE_FOR_PICKUP') {
        console.log("No token found, attempting to generate one");
        await regenerateQRCode();
      } else if (orderStatus === 'PAID') {
        setError("Votre commande est confirmée. Le QR code sera disponible dès que le vendeur aura déposé le produit au point relais.");
      } else {
        setError("Cette commande n'est pas encore prête pour le retrait.");
      }
    } catch (err: any) {
      console.error("Error fetching QR code:", err);
      setError(err.message || "Erreur lors du chargement du QR code");
    } finally {
      setLoading(false);
    }
  };

  const regenerateQRCode = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[ClientPickupQRCode] Calling regenerate-qr-code for order:', orderId);

      const { data, error: invokeError } = await supabase.functions.invoke('regenerate-qr-code', {
        body: { orderId }
      });

      console.log('[ClientPickupQRCode] Response:', { data, error: invokeError });

      if (invokeError) {
        console.error('[ClientPickupQRCode] Invoke error:', invokeError);
        throw new Error(invokeError.message || "Erreur lors de l'appel de la fonction");
      }

      if (data?.success && data?.token) {
        setQrSvg(data.token.qr_svg);
        setOtpCode(data.token.otp_code);
        setExpiresAt(data.token.expires_at);
        toast.success("QR code généré avec succès");
      } else {
        const errorMsg = data?.error || "Impossible de générer le QR code";
        console.error('[ClientPickupQRCode] Function returned error:', errorMsg);
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      console.error("[ClientPickupQRCode] Error regenerating QR code:", err);
      const errorMessage = err.message || "Erreur lors de la régénération du QR code";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyOTP = async () => {
    if (otpCode) {
      await navigator.clipboard.writeText(otpCode);
      setCopied(true);
      toast.success("Code copié !");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (!qrSvg) return;
    
    // Create a blob from the SVG and download
    const blob = new Blob([qrSvg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `qr-retrait-${orderNumber}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatExpiryDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[95vh] overflow-y-auto p-3 sm:p-6">
          <DialogHeader className="space-y-1 pb-2">
            <DialogTitle className="flex items-center gap-2 text-sm md:text-base">
              <QrCode className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">QR Code de retrait</span>
            </DialogTitle>
            <DialogDescription className="text-xs line-clamp-1">
              {orderNumber} - {productTitle}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 md:space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : error ? (
              <div className="text-center py-6 md:py-8">
                {orderStatus === 'PAID' ? (
                  <>
                    <Clock className="h-10 w-10 md:h-12 md:w-12 text-amber-500 mx-auto mb-3 md:mb-4" />
                    <p className="font-medium text-amber-600 mb-2 text-sm md:text-base">En attente de dépôt</p>
                    <p className="text-xs md:text-sm text-muted-foreground px-2">{error}</p>
                  </>
                ) : (
                  <>
                    <Package className="h-10 w-10 md:h-12 md:w-12 text-muted-foreground mx-auto mb-3 md:mb-4" />
                    <p className="text-xs md:text-sm text-muted-foreground mb-4 px-2">{error}</p>
                    {orderStatus === 'AVAILABLE_FOR_PICKUP' && (
                      <Button onClick={regenerateQRCode} disabled={loading} className="text-xs md:text-sm">
                        <RefreshCw className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                        Générer le QR code
                      </Button>
                    )}
                  </>
                )}
              </div>
            ) : qrSvg ? (
              <>
                {/* Status Badge */}
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-2">
                    <div className="flex items-center justify-center gap-1.5 text-green-700">
                      <Package className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="font-medium text-[11px] md:text-xs">
                        ✅ Prête pour retrait
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* QR Code Display */}
                <Card className="border-2 border-primary/20">
                  <CardContent className="p-2 md:p-3 flex justify-center bg-white">
                    <div className="w-full max-w-[220px] md:max-w-[240px] flex justify-center items-center">
                      <img 
                        src={qrSvg}
                        alt="QR Code de retrait"
                        className="w-full h-auto"
                        style={{ maxWidth: '240px', aspectRatio: '1' }}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Expiry Info */}
                {expiresAt && (
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="p-1.5 md:p-2">
                      <p className="text-[11px] md:text-xs text-center text-amber-700 font-medium">
                        ⏰ Valide jusqu'au {formatExpiryDate(expiresAt)}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* OTP Code Display (if available) */}
                {otpCode && (
                  <Card className="bg-orange-50 border-orange-200">
                    <CardContent className="p-2">
                      <div className="text-center">
                        <p className="text-[11px] text-orange-700 mb-1 font-medium">
                          🔢 Code: <span className="text-lg font-mono font-bold tracking-wider ml-1">{otpCode}</span>
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Instructions */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-2">
                    <p className="font-semibold mb-1 text-blue-900 text-[11px]">
                      📋 Instructions :
                    </p>
                    <div className="space-y-1 text-blue-800 text-[10px]">
                      <div className="flex gap-1">
                        <span className="font-bold">1.</span>
                        <span>Présentez ce QR au point relais</span>
                      </div>
                      <div className="flex gap-1">
                        <span className="font-bold">2.</span>
                        <span>L'agent scannera pour valider</span>
                      </div>
                      <div className="flex gap-1">
                        <span className="font-bold">3.</span>
                        <span>Vérifiez votre colis avant de partir</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-1">
                  <Button
                    variant="outline"
                    onClick={handleDownload}
                    className="flex-1 text-[11px] h-8"
                  >
                    <Download className="h-3 w-3 mr-1" />
                    Télécharger
                  </Button>
                  <Button
                    variant="outline"
                    onClick={regenerateQRCode}
                    disabled={loading}
                    className="flex-1 text-[11px] h-8"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Régénérer
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-6 md:py-8">
                <Package className="h-10 w-10 md:h-12 md:w-12 text-muted-foreground mx-auto mb-3 md:mb-4" />
                <p className="text-muted-foreground mb-4 text-xs md:text-sm px-2">
                  Aucun QR code disponible
                </p>
                {orderStatus === 'AVAILABLE_FOR_PICKUP' && (
                  <Button onClick={regenerateQRCode} disabled={loading} className="text-xs md:text-sm">
                    <QrCode className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                    Générer le QR code
                  </Button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
  );
};
